/**
 * Hooks de TanStack Query para cursos, módulos, lecciones y progreso.
 *
 * El RLS del schema enforce qué cursos puede leer cada usuario según su
 * suscripción (free/pro/premium), así que los queries simplemente piden
 * "todos" — el filtro pasa en la base de datos.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';
import { track } from '@/lib/analytics';
import type { SubscriptionPlan } from '@gurumba/schemas';

// =====================================================================
// Tipos
// =====================================================================

/** Nivel del curso en español ("intermediate" → "Intermedio"). */
export function nivelCurso(level: string | null | undefined): string {
  const NIVEL: Record<string, string> = {
    beginner: 'Principiante',
    intermediate: 'Intermedio',
    advanced: 'Avanzado',
    all: 'Todos los niveles',
  };
  return NIVEL[level ?? ''] ?? (level ?? '');
}

export interface Course {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string;
  cover_image_url: string | null;
  trailer_video_url: string | null;
  category_id: string | null;
  instructor_id: string | null;
  level: 'beginner' | 'intermediate' | 'advanced' | 'all';
  language: string;
  duration_minutes: number;
  duration_text: string | null;       // "1h 30m" del WP
  lesson_count: number;
  has_video_lessons: boolean;          // true si al menos 1 lesson tiene video
  min_plan_required: SubscriptionPlan;
  // Marketplace (Sprint 10.18): pricing_model='paid' → pago único del guru
  pricing_model: 'subscription' | 'paid';
  price_usd_cents: number | null;
  is_published: boolean;
  tags: string[];
  benefits: string | null;             // "qué aprenderás" multi-línea (TutorLMS)
  target_audience: string | null;      // "a quién va dirigido" multi-línea
  enable_qa: boolean;
  is_public: boolean;
  published_at: string | null;
  is_featured?: boolean;
  featured_rank?: number | null;
  // Enriquecido (cuando viene de useCourse/useCourses)
  instructor?: {
    id: string;
    full_name: string | null;
    handle: string | null;
    avatar_url: string | null;
  } | null;
}

export interface CourseModule {
  id: string;
  course_id: string;
  title: string;
  description: string | null;
  sort_order: number;
}

/**
 * Temario: lo que se puede mostrar sin haber pagado. Columnas seguras
 * únicamente — las trae la vista `course_lessons_public`.
 */
export interface LessonOutline {
  id: string;
  module_id: string;
  course_id: string;
  title: string;
  description: string | null;
  lesson_type: 'video' | 'text' | 'quiz' | 'assignment' | 'download' | 'live';
  duration_seconds: number;
  is_preview: boolean;
  sort_order: number;
}

/**
 * Lección completa, con el contenido. Solo la devuelve la tabla
 * `course_lessons`, que desde el 2026-08-25 exige inscripción (o ser el
 * guru autor / admin). Ver migración close_course_content_anon_leak.
 */
export interface Lesson extends LessonOutline {
  video_url: string | null;
  video_provider: 'supabase' | 'youtube' | 'vimeo' | 'mux' | 'cloudflare' | null;
  body_md: string | null;
}

/** Columnas que expone la vista pública del temario. */
const OUTLINE_COLS =
  'id, module_id, course_id, title, description, lesson_type, duration_seconds, is_preview, sort_order';

export interface Quiz {
  id: string;
  module_id: string;
  course_id: string;
  title: string;
  description: string | null;
  passing_grade: number;
  attempts_allowed: number;          // 0 = ilimitado
  time_limit_seconds: number;         // 0 = sin límite
  feedback_mode: 'default' | 'reveal' | 'retry';
  sort_order: number;
}

export interface Assignment {
  id: string;
  module_id: string;
  course_id: string;
  title: string;
  description: string | null;
  total_mark: number;
  pass_mark: number;
  time_duration_seconds: number;
  upload_files_limit: number;
  upload_file_size_limit_mb: number;
  sort_order: number;
}

/**
 * Item unificado dentro de un módulo: lesson | quiz | assignment.
 * Tiene `kind` como discriminador para renderizar el icono/badge correcto.
 * Se ordena por `sort_order` (el menu_order de TutorLMS).
 */
export type ModuleItem =
  // El item del temario es la versión sin contenido: la navegación del curso
  // no necesita body_md ni video_url, y así no viajan a quien no pagó.
  | (LessonOutline & { kind: 'lesson' })
  | (Quiz        & { kind: 'quiz' })
  | (Assignment  & { kind: 'assignment' });

export interface LessonProgress {
  lesson_id: string;
  last_position_sec: number;
  completed_at: string | null;
  total_watch_sec: number;
  last_accessed_at: string;
}

export interface CourseProgressRow {
  user_id: string;
  course_id: string;
  lessons_completed: number;
  lessons_total: number;
  pct_complete: number;
  last_accessed_at: string;
}

// =====================================================================
// Hooks de lectura
// =====================================================================

/**
 * Catálogo de cursos publicados. RLS filtra automaticamente según el plan.
 */
// =====================================================================
// Catálogo tipo Netflix: agrupa cursos publicados en filas.
// =====================================================================
export interface CatalogCategory {
  id: string;
  slug: string;
  name: string;
  courses: Course[];
}
export interface CourseCatalogData {
  featured: Course[];   // marcados por admin (is_featured), ordenados por rank
  recent: Course[];     // publicados en los últimos 30 días ("Recién llegados")
  paid: Course[];       // cursos de pago
  categories: CatalogCategory[]; // una fila por categoría con cursos
}

/** Días que un curso conserva el badge "Nuevo / Recién llegado". */
export const NEW_COURSE_DAYS = 30;

export function isNewCourse(c: Pick<Course, 'published_at'>): boolean {
  if (!c.published_at) return false;
  return Date.now() - new Date(c.published_at).getTime() < NEW_COURSE_DAYS * 24 * 3600 * 1000;
}

/**
 * Consulta ULTRA liviana para el reel de la coming-soon: solo id + título +
 * portada, de cursos publicados con imagen (máx 18). Reemplaza al catálogo
 * completo (`select('*')` × 300 filas) que hacía lenta la página de expectativa.
 */
export function useReelCourses() {
  return useQuery({
    queryKey: ['reel-courses'],
    staleTime: 300_000,
    queryFn: async (): Promise<{ id: string; title: string; cover_image_url: string | null }[]> => {
      // Destacados primero (curados por admin), luego recientes de relleno.
      const { data, error } = await supabase
        .from('courses')
        .select('id, title, cover_image_url')
        .eq('is_published', true)
        .is('deleted_at', null)
        .not('cover_image_url', 'is', null)
        .order('is_featured', { ascending: false })
        .order('featured_rank', { ascending: true, nullsFirst: false })
        .order('published_at', { ascending: false })
        .limit(18);
      if (error) throw error;
      return (data ?? []) as { id: string; title: string; cover_image_url: string | null }[];
    },
  });
}

export function useCourseCatalog() {
  return useQuery({
    queryKey: ['course-catalog'],
    staleTime: 60_000,
    queryFn: async (): Promise<CourseCatalogData> => {
      const [catsRes, coursesRes] = await Promise.all([
        supabase.from('course_categories').select('id, slug, name, sort_order').order('sort_order'),
        supabase.from('courses').select('*')
          .eq('is_published', true)
          .is('deleted_at', null)
          .order('published_at', { ascending: false })
          .limit(300),
      ]);
      const raw = (coursesRes.data ?? []) as Course[];

      // Enriquecer con el autor (instructor) para mostrarlo en las cards.
      const instructorIds = [...new Set(raw.map((c) => c.instructor_id).filter(Boolean))] as string[];
      let profMap = new Map<string, { id: string; full_name: string | null; handle: string | null; avatar_url: string | null }>();
      if (instructorIds.length > 0) {
        const { data: profs } = await supabase
          .from('profiles')
          .select('id, full_name, handle, avatar_url')
          .in('id', instructorIds);
        profMap = new Map((profs ?? []).map((p) => [p.id, p]));
      }
      const list: Course[] = raw.map((c) => ({
        ...c,
        instructor: c.instructor_id ? (profMap.get(c.instructor_id) ?? null) : null,
      }));

      const featured = list
        .filter((c) => c.is_featured)
        .sort((a, b) => (a.featured_rank ?? 9999) - (b.featured_rank ?? 9999));
      const recent = list.filter((c) => isNewCourse(c));
      const paid = list.filter((c) => c.pricing_model === 'paid');

      const categories: CatalogCategory[] = ((catsRes.data ?? []) as { id: string; slug: string; name: string }[])
        .map((cat) => ({
          id: cat.id,
          slug: cat.slug,
          name: cat.name,
          courses: list.filter((c) => c.category_id === cat.id),
        }))
        .filter((cat) => cat.courses.length > 0);

      return { featured, recent, paid, categories };
    },
  });
}

export function useCourses(filters: {
  search?: string;
  level?: Course['level'];
  category_slug?: string;
} = {}) {
  return useQuery({
    queryKey: ['courses', filters],
    queryFn: async (): Promise<Course[]> => {
      let query = supabase
        .from('courses')
        .select('*')
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('published_at', { ascending: false });

      if (filters.search) {
        query = query.ilike('title', `%${filters.search}%`);
      }
      if (filters.level) {
        query = query.eq('level', filters.level);
      }
      if (filters.category_slug) {
        const { data: cat } = await supabase
          .from('course_categories')
          .select('id')
          .eq('slug', filters.category_slug)
          .maybeSingle();
        if (cat) query = query.eq('category_id', cat.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      if (!data || data.length === 0) return [];

      // Enriquecer con instructor (1 query separada + merge)
      const instructorIds = [...new Set(data.map((c) => c.instructor_id).filter((x): x is string => !!x))];
      const { data: profiles } = instructorIds.length
        ? await supabase.from('profiles').select('id, full_name, handle, avatar_url').in('id', instructorIds)
        : { data: [] };
      const instructorMap = new Map((profiles ?? []).map((p) => [p.id, p]));

      return data.map((c) => ({
        ...c,
        instructor: c.instructor_id ? instructorMap.get(c.instructor_id) ?? null : null,
      })) as Course[];
    },
  });
}

/**
 * Detalle de un curso por slug.
 */
export function useCourse(slug: string) {
  return useQuery({
    queryKey: ['course', slug],
    queryFn: async () => {
      // RPC de 1 solo round trip (curso + instructor). Antes eran 2 queries
      // encadenadas y el "Cargando…" del detalle sumaba ambos viajes.
      // SECURITY INVOKER: las policies RLS siguen mandando.
      const { data, error } = await supabase.rpc('get_course_public', { _slug: slug });
      if (error) throw error;
      if (!data) return null;
      const row = data as any;
      return { ...row, instructor: row.instructor ?? null } as Course;
    },
    enabled: !!slug,
    staleTime: 2 * 60_000,
  });
}

/**
 * Estructura del curso: módulos + lecciones + quizzes + assignments.
 * Cada item lleva un `kind` discriminador para que el frontend renderice
 * el icono adecuado. Dentro de un módulo se ordenan TODOS los items
 * (lessons + quizzes + assignments) por `sort_order` para preservar
 * el flujo natural de TutorLMS (lección 1 → 2 → quiz → assignment).
 */
export function useCourseContent(courseId: string | undefined) {
  return useQuery({
    queryKey: ['course-content', courseId],
    queryFn: async () => {
      if (!courseId) {
        return { modules: [], lessons: [], quizzes: [], assignments: [], itemsByModule: {} };
      }

      const [modulesRes, lessonsRes, quizzesRes, assignmentsRes] = await Promise.all([
        supabase.from('course_modules').select('*').eq('course_id', courseId).order('sort_order'),
        // Temario por la vista pública: nunca trae body_md ni video_url.
        // El contenido real lo pide `useLesson`, ya con la inscripción validada.
        supabase.from('course_lessons_public').select(OUTLINE_COLS).eq('course_id', courseId).order('sort_order'),
        supabase.from('quizzes').select('*').eq('course_id', courseId).order('sort_order'),
        supabase.from('assignments').select('*').eq('course_id', courseId).order('sort_order'),
      ]);

      if (modulesRes.error) throw modulesRes.error;
      if (lessonsRes.error) throw lessonsRes.error;
      if (quizzesRes.error) throw quizzesRes.error;
      if (assignmentsRes.error) throw assignmentsRes.error;

      const modules = modulesRes.data as CourseModule[];
      const lessons = lessonsRes.data as LessonOutline[];
      const quizzes = quizzesRes.data as Quiz[];
      const assignments = assignmentsRes.data as Assignment[];

      // Construir mapa module_id → items[] ya ordenado por sort_order
      const itemsByModule: Record<string, ModuleItem[]> = {};
      for (const m of modules) {
        const items: ModuleItem[] = [
          ...lessons.filter((l) => l.module_id === m.id).map((l): ModuleItem => ({ ...l, kind: 'lesson' })),
          ...quizzes.filter((q) => q.module_id === m.id).map((q): ModuleItem => ({ ...q, kind: 'quiz' })),
          ...assignments.filter((a) => a.module_id === m.id).map((a): ModuleItem => ({ ...a, kind: 'assignment' })),
        ];
        items.sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
        itemsByModule[m.id] = items;
      }

      return { modules, lessons, quizzes, assignments, itemsByModule };
    },
    enabled: !!courseId,
    // La estructura del curso solo cambia cuando el admin edita: cachear.
    staleTime: 5 * 60_000,
  });
}

/**
 * Detalle de una lección.
 */
export function useLesson(lessonId: string | undefined) {
  return useQuery({
    queryKey: ['lesson', lessonId],
    queryFn: async () => {
      if (!lessonId) return null;
      const { data, error } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('id', lessonId)
        .maybeSingle();
      if (error) throw error;
      return data as Lesson | null;
    },
    enabled: !!lessonId,
  });
}

/**
 * Progreso del usuario actual sobre TODAS sus lecciones de un curso.
 */
export function useLessonProgressByCourse(courseId: string | undefined) {
  return useQuery({
    queryKey: ['lesson-progress', courseId],
    queryFn: async () => {
      if (!courseId) return {} as Record<string, LessonProgress>;
      const { data, error } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('course_id', courseId);
      if (error) throw error;
      const map: Record<string, LessonProgress> = {};
      for (const row of data as LessonProgress[]) {
        map[row.lesson_id] = row;
      }
      return map;
    },
    enabled: !!courseId,
  });
}

/**
 * Vista agregada de progreso por curso (% completado).
 */
export function useMyCoursesProgress() {
  const user = useUser();
  return useQuery({
    queryKey: ['my-courses-progress', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_course_progress')
        .select('*');
      if (error) throw error;
      const map: Record<string, CourseProgressRow> = {};
      for (const row of (data ?? []) as CourseProgressRow[]) {
        map[row.course_id] = row;
      }
      return map;
    },
  });
}

/**
 * Sprint 10.48 — Consolidación académica de UN curso para el estudiante.
 * Llama a la RPC get_course_final_summary y devuelve el jsonb con:
 *   · progreso, nota final, certificado emitido
 *   · counters lessons / quizzes / assignments
 *
 * La pantalla "épica fin de curso" la consume cuando is_completed=true.
 */
export interface CourseFinalSummary {
  course_id: string;
  course_slug: string;
  course_title: string;
  cover_image_url: string | null;
  user_id: string;
  enrolled_at: string | null;
  is_enrolled: boolean;
  lesson_total: number;
  lesson_completed: number;
  quiz_total: number;
  quiz_passed: number;
  quiz_grade_pct: number | null;
  assignment_total: number;
  assignment_passed: number;
  assignment_grade_pct: number | null;
  items_total: number;
  items_completed: number;
  progress_pct: number;
  grade_pct: number | null;
  is_completed: boolean;
  certificate_id: string | null;
  certificate_code: string | null;
  certificate_issued_at: string | null;
}

export function useCourseFinalSummary(courseId: string | undefined) {
  return useQuery({
    queryKey: ['course-final-summary', courseId],
    queryFn: async (): Promise<CourseFinalSummary | null> => {
      if (!courseId) return null;
      const { data, error } = await supabase.rpc('get_course_final_summary', {
        _course_id: courseId,
      });
      if (error) throw error;
      return data as CourseFinalSummary;
    },
    enabled: !!courseId,
  });
}

/**
 * Sprint 10.48 — Lista TODOS los cursos del estudiante con su summary
 * consolidado. Para la página "Mi historial".
 */
export function useMyCoursesSummary() {
  const user = useUser();
  return useQuery({
    queryKey: ['my-courses-summary', user?.id],
    queryFn: async (): Promise<CourseFinalSummary[]> => {
      const { data, error } = await supabase.rpc('get_my_courses_summary');
      if (error) throw error;
      return (data ?? []) as CourseFinalSummary[];
    },
  });
}

/**
 * Sprint 10.52 #4 — Detalle de notas por quiz/assignment para un curso.
 * Devuelve listas individuales que alimentan la página de detalle
 * de Mi historial (`/dashboard/my-courses/:slug`).
 */
export interface QuizBreakdownItem {
  id: string;
  title: string;
  module_id: string;
  module_title: string | null;
  sort_order: number;
  passing_grade: number;
  best_score_percent: number | null;
  best_score_points: number | null;
  best_max_points: number | null;
  best_passed: boolean | null;
  best_completed_at: string | null;
  attempts_count: number;
}

export interface AssignmentBreakdownItem {
  id: string;
  title: string;
  module_id: string;
  module_title: string | null;
  sort_order: number;
  total_mark: number;
  pass_mark: number;
  grade: number | null;
  score_percent: number | null;
  passed: boolean | null;
  attempt_number: number | null;
  status: string | null;
  graded_at: string | null;
}

export interface CourseGradeBreakdown {
  quizzes: QuizBreakdownItem[];
  assignments: AssignmentBreakdownItem[];
}

export function useMyCourseGradeBreakdown(courseId: string | undefined) {
  const user = useUser();
  return useQuery({
    queryKey: ['my-course-grade-breakdown', user?.id, courseId],
    queryFn: async (): Promise<CourseGradeBreakdown> => {
      if (!courseId) return { quizzes: [], assignments: [] };
      const { data, error } = await supabase.rpc('get_my_course_grade_breakdown', {
        _course_id: courseId,
      });
      if (error) throw error;
      const raw = (data ?? { quizzes: [], assignments: [] }) as Partial<CourseGradeBreakdown>;
      return {
        quizzes: raw.quizzes ?? [],
        assignments: raw.assignments ?? [],
      };
    },
    enabled: !!courseId,
  });
}

/**
 * Categorías para los filtros del catálogo (público + interno cliente).
 *
 * Devuelve SOLO categorías que tienen al menos 1 curso publicado, para
 * que el usuario nunca vea chips/filtros vacíos. Las categorías sin
 * publicados (drafts puros, o creadas pero todavía sin contenido)
 * siguen existiendo en DB y se ven desde admin (useAdminCategories),
 * pero no se exponen acá.
 *
 * Implementación: 2 queries. La primera trae todas las categorías
 * ordenadas; la segunda los category_id distintos de cursos publicados.
 * Filtramos en cliente. Para el volumen actual (~500 cursos / ~10 cats)
 * es despreciable; si en el futuro hay miles de cursos vale la pena
 * convertir a RPC. Cache de 5 min — los chips no cambian seguido.
 */
export function useCourseCategories() {
  return useQuery({
    queryKey: ['course-categories', 'public_with_published'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [catsRes, publishedRes] = await Promise.all([
        supabase
          .from('course_categories')
          .select('*')
          .order('sort_order'),
        supabase
          .from('courses')
          .select('category_id')
          .eq('is_published', true)
          .not('category_id', 'is', null),
      ]);
      if (catsRes.error) throw catsRes.error;
      if (publishedRes.error) throw publishedRes.error;

      const usedIds = new Set(
        (publishedRes.data ?? []).map((c) => c.category_id as string),
      );
      const all = (catsRes.data ?? []) as { id: string; slug: string; name: string }[];
      return all.filter((c) => usedIds.has(c.id));
    },
  });
}

// =====================================================================
// Mutaciones de progreso
// =====================================================================

/**
 * Upsert de lesson_progress. Llamado:
 * - Cada 15s con last_position_sec
 * - Al 90% con completed=true
 */
export function useUpdateLessonProgress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      lesson_id: string;
      course_id: string;
      last_position_sec: number;
      watch_seconds_delta?: number;
      completed?: boolean;
    }) => {
      const { data: userRes } = await supabase.auth.getUser();
      if (!userRes.user) throw new Error('Sin sesión');

      const payload: Record<string, unknown> = {
        user_id: userRes.user.id,
        lesson_id: input.lesson_id,
        course_id: input.course_id,
        last_position_sec: input.last_position_sec,
        last_accessed_at: new Date().toISOString(),
      };
      if (input.completed) {
        payload.completed_at = new Date().toISOString();
      }
      // first_started_at solo en INSERT (no en UPDATE). Lo gestionamos
      // chequeando si la fila ya existe.
      const { data: existing } = await supabase
        .from('lesson_progress')
        .select('first_started_at')
        .eq('user_id', userRes.user.id)
        .eq('lesson_id', input.lesson_id)
        .maybeSingle();
      if (!existing) {
        payload.first_started_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('lesson_progress')
        .upsert(payload, { onConflict: 'user_id,lesson_id', ignoreDuplicates: false });
      if (error) {
        console.error('lesson_progress upsert error:', error);
        throw new Error(error.message);
      }
    },
    onSuccess: (_data, vars) => {
      if (vars.completed) {
        track('lesson_completed', { lesson_id: vars.lesson_id, course_id: vars.course_id });
      }
      queryClient.invalidateQueries({ queryKey: ['lesson-progress', vars.course_id] });
      queryClient.invalidateQueries({ queryKey: ['my-courses-progress'] });
      // CRÍTICO: el gate de progresión secuencial lee de aquí.
      // Si no lo invalidamos, al navegar a la siguiente lección/quiz/asignación
      // el cache cree que ésta no se completó y dispara el candado.
      queryClient.invalidateQueries({ queryKey: ['course-item-completion', vars.course_id] });
    },
  });
}

/**
 * Cuántos cursos están abiertos para el plan Free (Clasificación → Plan
 * mínimo = Free). Lo usan la tarjeta Free de /pricing y la landing: el
 * número sale de la base, así que cambia solo si el admin marca más o menos.
 */
export function useFreeCourseCount() {
  return useQuery({
    queryKey: ['free-course-count'],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('courses')
        .select('id', { count: 'exact', head: true })
        .eq('is_published', true)
        .is('deleted_at', null)
        .eq('pricing_model', 'subscription')
        .eq('min_plan_required', 'free');
      if (error) throw error;
      return count ?? 0;
    },
  });
}
